import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Dropdown from '../components/ui/Dropdown';
import { Image, Package, Store, CalendarDays, Wallet, Tag, Info, List, Grid, Search, MoreHorizontal, ArrowRightLeft, MoreVertical, ChevronDown, ChevronDownCircle } from 'lucide-react';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { fetchStores, fetchStoresWhereStaffAssigned, fetchInventoryForStoreSales, fetchInventoryForStore, API_ROOT_PATH } from '../apiHelper';

// Initialize the API client
const client = new FastAPIClient();

/**
 * InventoryItemCard Component for Grid View.
 * Displays a single inventory item's details in a card format.
 */
const InventoryItemCard = ({ item, onViewLots }) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const onHandQuantity = item.current_quantity;
    const isSaleable = item.catentry.buyable === 1;
    const hasExpiryDate = item.expiration_date && item.expiration_date !== '0001-01-01T00:00:00Z';
    const listPriceCurrencySymbol = item.catentry.listprice?.currency_rel?.setcnote || item.store.setccurr || '';
    const listPriceValue = item.catentry.listprice?.listprice || item.listprice || 0;
    const attachmentUrl = item.catentry.attachments?.[0]?.attachment?.attachmenturl;

    const navigateStockHistory = (catentry_id) => {
        if (catentry_id) {
            navigate('/reports/new-stock-history', { state: { catentryId: catentry_id } });
        } else {
            showToast("Could not find product ID", 'error');
        }
    };

    const navigateViewDetails = (catentry_id) => {
        if (catentry_id) {
            navigate('/catalog/view-details', { state: { catentryId: catentry_id } });
        } else {
            showToast("Could not find product ID", 'error');
        }
    };

    const navigateViewLots = () => {
        if(item.catentry_id && item.store_id) {
            const state = {mergedItem: item}
            navigate('/inventory/lots', {
                state: state
            });
        } else {
            showToast("Could not find product or store ID for lots view", "error");
        }
    };

    return (
        <Card className="flex flex-col h-full overflow-hidden transition-all duration-200 ease-in-out hover:shadow-lg relative">
            <div className="absolute top-2 right-2 z-10">
                <Dropdown
                    trigger={<ChevronDownCircle size={20} className="text-gray-600 hover:text-gray-800" />}
                    align="right"
                >
                    {/* <button
                        onClick={() => navigateViewDetails(item.catentry_id)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                        View Details
                    </button> */}
                    <button
                        onClick={onViewLots}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                        View Inventory Lots
                    </button>
                    <button
                        onClick={() => navigateStockHistory(item.catentry_id)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                        View Stock History
                    </button>
                </Dropdown>
            </div>
            <div className="relative w-full h-40 bg-gray-100 flex items-center justify-center rounded-t-lg overflow-hidden">
                {attachmentUrl ? (
                    <img
                        src={`${API_ROOT_PATH}${attachmentUrl}`}
                        alt={item.catentry.productname}
                        className="w-full h-full object-cover select-none pointer-events-none"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable="false"
                    />
                ) : (
                    <Image size={64} className="text-gray-400" />
                )}
                <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold
                            ${isSaleable ? 'bg-gmail-green text-white' : 'bg-gmail-red text-white'}`}>
                    {isSaleable ? 'For Sale' : 'Internal Use'}
                </div>
            </div>
            <div className="p-4 flex-1 flex flex-col justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-1 truncate" title={item.catentry.productname}>
                        {item.catentry.productname}
                    </h3>
                    <p className="text-sm text-text-secondary mb-3 truncate" title={item.catentry.partnumber}>
                        Part No: {item.catentry.partnumber || 'N/A'}
                    </p>
                    <div className="flex items-center gap-2 text-text-primary text-sm mb-1">
                        <Store size={16} className="text-gmail-red" />
                        <span className="font-medium">{item.store.identifier}</span>
                    </div>
                    <div className="flex items-center gap-2 text-text-primary text-sm mb-1">
                        <Package size={16} className="text-gmail-yellow" />
                        <span className="font-medium">Quantity: {onHandQuantity} {item.catentry.unitofmeasure?.name || 'Units'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-text-primary text-sm mb-1">
                        <Wallet size={16} className="text-gmail-green" />
                        <span className="font-medium">List: {listPriceCurrencySymbol} {listPriceValue?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-text-primary text-sm mb-1">
                        <Tag size={16} className="text-purple-600" />
                        <span className="font-medium">Cost: {item.unit_cost?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-text-primary text-sm mb-1">
                        <CalendarDays size={16} className="text-gray-600" />
                        <span className="font-medium">Receipt: {new Date(item.receipt_datetime).toLocaleDateString()}</span>
                    </div>
                    {hasExpiryDate && (
                        <div className="flex items-center gap-2 text-text-primary text-sm">
                            <CalendarDays size={16} className="text-orange-500" />
                            <span className="font-medium">Expiry: {new Date(item.expiration_date).toLocaleDateString()}</span>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

/**
 * Inventory Component for seeing all inventories in a store.
 * Allows selection of a store, displays inventory items in either a grid or list view,
 * and supports pagination.
 */
const Inventory = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [loggedInUser, setLoggedInUser] = useState(null);
    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [inventoryItems, setInventoryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(100);
    const [hasMore, setHasMore] = useState(true);
    const [viewMode, setViewMode] = useState('grid');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            setError(null);
            const lcUser = localStorage.getItem("user");
            let parsedUser = null;

            if (lcUser) {
                parsedUser = JSON.parse(lcUser);
                setLoggedInUser(parsedUser);
            } else {
                showToast("Please log in to view inventory.", "info");
                setLoading(false);
                navigate('/login');
                return;
            }

            try {
                // const fetchedStores = await fetchStores(parsedUser);
                const fetchedStores = await fetchStoresWhereStaffAssigned(parsedUser);
                setStores(fetchedStores.map(store => ({
                    label: store.identifier,
                    value: store.store_id,
                })));
                if (fetchedStores.length > 0) {
                    setSelectedStoreId(fetchedStores[0].store_id);
                }
            } catch (err) {
                console.error("Error loading stores:", err);
                setError("Failed to load stores. Please try again.");
                showToast("Failed to load stores.", "error");
            } finally {
                setLoading(false);
            }
        };
        loadInitialData();
    }, [showToast, navigate]);

    useEffect(() => {
        const loadInventory = async () => {
            if (!loggedInUser || !selectedStoreId) {
                setInventoryItems([]);
                setHasMore(false);
                return;
            }

            setLoading(true);
            setError(null);

            const payload = {
                users_id: loggedInUser.users_id,
                logon_id: loggedInUser.logon_id,
                hpwc: loggedInUser.hpwc,
                store_id: selectedStoreId,
                offset: offset,
                limit: limit,
            };

            try {
                // const newItems = await fetchInventoryForStore(payload);
                const newItems = await fetchInventoryForStoreSales(payload);
                // console.log(newItems)
                setInventoryItems(prevItems => (offset === 0 ? newItems : [...prevItems, ...newItems]));
                setHasMore(newItems.length === limit);
            } catch (err) {
                console.error("Error fetching inventory:", err);
                setError("Failed to load inventory items. Please try again.");
                showToast("Failed to load inventory.", "error");
            } finally {
                setLoading(false);
            }
        };

        loadInventory();
    }, [loggedInUser, selectedStoreId, offset, limit, showToast]);

    const navigateStockHistory = (catentry_id) => {
        if (catentry_id) {
            navigate('/reports/new-stock-history', { state: { catentryId: catentry_id } });
        } else {
            showToast("Could not find product ID", 'error');
        }
    };

    const navigateViewDetails = (catentry_id) => {
        if (catentry_id) {
            navigate('/catalog/view-details', { state: { catentryId: catentry_id } });
        } else {
            showToast("Could not find product ID", 'error');
        }
    };

    const navigateViewLots = (item) => {
        if (item.catentry_id && item.store_id) {
            navigate('/inventory/lots', {
                state: {
                    selectedItem: item,
                    allItems: filteredInventoryItems
                }
            });
        } else {
            showToast("Could not find product or store ID for lots view", 'error');
        }
    }

    const handleStoreChange = (e) => {
        const newStoreId = parseInt(e.target.value, 10);
        setSelectedStoreId(newStoreId);
        setOffset(0);
        setInventoryItems([]);
        setHasMore(true);
    };

    const handleLoadMore = () => {
        setOffset(prevOffset => prevOffset + limit);
    };

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
    };

    // const filteredInventoryItems = useMemo(() => {
    //     if (!searchTerm) {
    //         return inventoryItems;
    //     }
    //     const lowerCaseSearchTerm = searchTerm.toLowerCase();
    //     return inventoryItems.filter(item =>
    //         item.catentry.productname.toLowerCase().includes(lowerCaseSearchTerm) ||
    //         (item.catentry.partnumber && item.catentry.partnumber.toLowerCase().includes(lowerCaseSearchTerm)) ||
    //         (item.store.identifier && item.store.identifier.toLowerCase().includes(lowerCaseSearchTerm))
    //     );
    // }, [inventoryItems, searchTerm]);

    // const filteredInventoryItems = useMemo(() => {
    //     // Sort items by the most recent transaction_datetime in inventory_transactions
    //     const sortedItems = [...inventoryItems].sort((a, b) => {
    //         const aDate = a.inventory_transactions?.[0]?.transaction_datetime || a.receipt_datetime;
    //         const bDate = b.inventory_transactions?.[0]?.transaction_datetime || b.receipt_datetime;
    //         // Sort by partnumber first to group identical products, then by transaction_datetime (descending)
    //         const partnumberComparison = (a.catentry.partnumber || '').localeCompare(b.catentry.partnumber || '');
    //         if (partnumberComparison !== 0) return partnumberComparison;
    //         return new Date(bDate) - new Date(aDate);
    //     });

    //     // Apply search filter
    //     if (!searchTerm) {
    //         return sortedItems;
    //     }
    //     const lowerCaseSearchTerm = searchTerm.toLowerCase();
    //     return sortedItems.filter(item =>
    //         item.catentry.productname.toLowerCase().includes(lowerCaseSearchTerm) ||
    //         (item.catentry.partnumber && item.catentry.partnumber.toLowerCase().includes(lowerCaseSearchTerm)) ||
    //         (item.store.identifier && item.store.identifier.toLowerCase().includes(lowerCaseSearchTerm))
    //     );
    // }, [inventoryItems, searchTerm]);

    const filteredInventoryItems = useMemo(() => {
        // Group items by catentry_id and partnumber
        const groupedItems = inventoryItems.reduce((acc, item) => {
            const key = `${item.catentry_id}-${item.catentry.partnumber}`;
            if (!acc[key]) {
                acc[key] = {
                    ...item,
                    current_quantity: item.current_quantity,
                    receipt_datetime: item.receipt_datetime,
                    unit_cost: item.unit_cost,
                    lot_ids: [item.lot_id], // Track lot_ids for reference
                };
            } else {
                acc[key].current_quantity += item.current_quantity;
                // Use the most recent receipt_datetime
                if (new Date(item.receipt_datetime) > new Date(acc[key].receipt_datetime)) {
                    acc[key].receipt_datetime = item.receipt_datetime;
                    acc[key].unit_cost = item.unit_cost; // Update to latest unit_cost
                    acc[key].goodsreceipt = item.goodsreceipt; // Update to latest goodsreceipt
                    acc[key].inventory_transactions = item.inventory_transactions; // Update to latest transactions
                }
                acc[key].lot_ids.push(item.lot_id);
            }
            return acc;
        }, {});

        // Convert grouped items to array
        let mergedItems = Object.values(groupedItems);

        // Apply search filter
        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            mergedItems = mergedItems.filter(item =>
                item.catentry.productname.toLowerCase().includes(lowerCaseSearchTerm) ||
                (item.catentry.partnumber && item.catentry.partnumber.toLowerCase().includes(lowerCaseSearchTerm)) ||
                (item.store.identifier && item.store.identifier.toLowerCase().includes(lowerCaseSearchTerm))
            );
        }

        // console.log(mergedItems)

        return mergedItems;
    }, [inventoryItems, searchTerm]);

    const inventoryTableColumns = useMemo(() => [
        { key: 'catentry.productname', label: 'Product Name' },
        { key: 'catentry.partnumber', label: 'Part Number' },
        { key: 'store.identifier', label: 'Branch' },
        {
            key: 'current_quantity',
            label: 'On-Hand Quantity',
            render: (value, row) => `${value} ${row.catentry.unitofmeasure?.name || 'Units'}`
        },
        {
            key: 'unit_cost',
            label: 'Unit Cost',
            render: (value, row) => `${row.store.setccurr} ${value?.toFixed(2) || '0.00'}`
        },
        {
            key: 'catentry.listprice.listprice',
            label: 'List Price',
            render: (value, row) => {
                const currencySymbol = row.catentry.listprice?.currency_rel?.setcnote || row.store.setccurr || '';
                return `${currencySymbol} ${value?.toFixed(2) || '0.00'}`;
            }
        },
        {
            key: 'receipt_datetime',
            label: 'Receipt Date',
            render: (value) => new Date(value).toLocaleDateString()
        },
        {
            key: 'expiration_date',
            label: 'Expiry Date',
            render: (value) => (value && value !== '0001-01-01T00:00:00Z' ? new Date(value).toLocaleDateString() : 'N/A')
        },
        {
            key: 'catentry.buyable',
            label: 'Saleable',
            render: (value) => (value === 1 ? 'Yes' : 'No (Internal Use)')
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (value, row) => (
                <Dropdown
                    trigger={<ChevronDownCircle size={20} className="text-gray-600 hover:text-gray-800" />}
                    align="right"
                >
                    {/* <button
                        onClick={() => navigateViewDetails(row.catentry_id)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                        View Details
                    </button> */}
                    <button
                        onClick={() => navigateViewLots(row)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        View Inventory Lots
                    </button>
                    <button
                        onClick={() => navigateStockHistory(row.catentry_id)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                        View Stock History
                    </button>
                </Dropdown>
            )
        },
    ], [navigate, navigateStockHistory, navigateViewDetails, navigateViewLots]);

    if (loading && inventoryItems.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <p className="text-text-secondary text-lg">Loading inventory data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
                <p className="text-gmail-red text-lg mb-4">
                    Error: {error}
                </p>
                <Button onClick={() => window.location.reload()} variant="primary">
                    Reload Page
                </Button>
            </div>
        );
    }

    if (stores.length === 0 && !loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
                <p className="text-text-secondary text-lg mb-4">
                    No branches found. Please create a branch first to manage inventory.
                </p>
                <Button onClick={() => navigate('/branches/create')} variant="primary">
                    Create Branch
                </Button>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <h1 className="text-3xl font-bold text-text-primary mb-6">Inventory Overview</h1>

            <Card className="mb-6 p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                <SearchableSelectBox
                    id="store_selection"
                    name="store_selection"
                    value={selectedStoreId}
                    onChange={handleStoreChange}
                    options={stores}
                    placeholder="Choose a branch"
                    containerClassName="w-full sm:w-auto sm:flex-1 mb-0"
                />
                <div className="flex items-start justify-center gap-3">
                    <Input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full sm:max-w-xs pl-10 pr-4 py-2 text-sm rounded-md border border-gray-300 focus:ring-gmail-blue focus:border-gmail-blue"
                        icon={<Search size={18} className="text-gray-dark absolute left-3 top-1/2 -translate-y-1/2" />}
                    />
                    {/* Grid / List toggle */}
                    <Button
                        variant={viewMode === 'grid' ? 'primary' : 'secondary'}
                        onClick={() => handleViewModeChange('grid')}
                        className="flex items-center gap-2 !py-3"
                    >
                        <Grid size={18} />
                    </Button>
                    <Button
                        variant={viewMode === 'list' ? 'primary' : 'secondary'}
                        onClick={() => handleViewModeChange('list')}
                        className="flex items-center gap-2 !py-3"
                    >
                        <List size={18} />
                    </Button>

                    {/* NEW: Inventory Transfer Button */}
                    <Button
                        onClick={() => navigate('/inventory/inventory-transfer')}
                        variant="danger"
                        className="flex items-center gap-2 whitespace-nowrap"
                        title="Transfer inventory between branches"
                    >
                        <ArrowRightLeft size={18} />
                        <span className="hidden sm:inline">Transfer</span>
                    </Button>
                </div>
            </Card>

            {filteredInventoryItems.length === 0 && !loading ? (
                <Card className="p-8 text-center text-text-secondary">
                    <Info size={48} className="mx-auto mb-4 text-gray-400" />
                    <p className="text-lg">No inventory items found for the selected branch or matching your search.</p>
                    <p className="text-sm mt-2">Try selecting a different branch, adjusting your search, or add new product items.</p>
                    <Button onClick={() => navigate('/catalog/create-item')} variant="secondary" className="mt-4">
                        Add New Product Item
                    </Button>
                </Card>
            ) : (
                <>
                    {viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                            {filteredInventoryItems.map((item, index) => (
                                <InventoryItemCard key={item.catentry_id + '-' + item.lot_id + '-' + index} item={item} onViewLots={() => navigateViewLots(item)} />
                            ))}
                        </div>
                    ) : (
                        <div className="relative overflow-y-hidden rounded-lg border border-gray-200 shadow-lg bg-white flex flex-col min-h-[60vh]">
                            <div className="overflow-auto flex-1 bg-white">
                                <table className="min-w-full text-sm text-gray-800 table-auto">
                                    <thead className="sticky top-0 bg-gray-50 z-10 shadow-sm">
                                        <tr>
                                            {inventoryTableColumns.map((col) => (
                                                <th
                                                    key={col.key}
                                                    className="px-4 py-3 text-left font-semibold text-gray-700 tracking-wide whitespace-nowrap">
                                                    {col.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {filteredInventoryItems.map((row, i) => (
                                            <tr key={row.catentry_id + '-' + row.lot_id + '-' + i} className="hover:bg-gray-50 transition-colors">
                                                {inventoryTableColumns.map((col) => (
                                                    <td key={col.key} className="px-4 py-3 whitespace-nowrap">
                                                        {col.render
                                                            ? col.render(
                                                                col.key.split('.').reduce((o, k) => (o || {})[k], row),
                                                                row
                                                            )
                                                            : (col.key.split('.').reduce((o, k) => (o || {})[k], row) !== undefined && col.key.split('.').reduce((o, k) => (o || {})[k], row) !== null
                                                                ? <span>{col.key.split('.').reduce((o, k) => (o || {})[k], row)}</span>
                                                                : <span className="text-gray-400">-</span>)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {hasMore && (
                        <div className="flex justify-center mt-6">
                            <Button
                                onClick={handleLoadMore}
                                disabled={loading}
                                variant="secondary"
                                className="px-6 py-3"
                            >
                                {loading ? 'Loading More...' : 'Load More'}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Inventory;